import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { ActionsContainer, SearchAndFilterContainer, StyledCell } from './styles';
import { Box, Button, IconButton, TableRow, Tooltip } from '@mui/material';
import { useState } from 'react';
import SearchBar from '../../../../Components/SearchBar/SearchBar';
import * as paths from '../../../../Routing/paths';
import { useTranslation } from 'react-i18next';
import { AssignCandidateDialog } from './Components/AssignCandidateDialog';
import CandidateFormDialog from '../../../../Components/Layouts/MainLayout/components/CandidateFormDialog';
import { candidateJobStatusOptions, JobStatus, StatusType } from '../../../../Types/constants';
import { StatusSelect } from '../../../../Components/StatusSelect/StatusSelect';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { ClearFiltersButton } from '../../../../Components/Common/ClearFiltersButton/ClearFiltersButton';
import { ConfirmActionDialog } from '../../../../Components/Common/ConfirmActionDialog/ConfirmActionDialog';
import { useMutation } from '@tanstack/react-query';
import { deleteCandidateJob } from '../../../../Services/CandidateJobs';
import toast from 'react-hot-toast';
import { DashboardScrollTable } from '../../../../Components/DashboardTable/DashboardScrollTable';
import React from 'react';
import { JOBS } from '../../../../Routing/paths';

// Función helper para validar si un substatus es válido para traducir
const isValidSubStatus = (subStatus: string | undefined): boolean => {
  if (!subStatus) return false;
  const validSubStatuses = [
    'PendingResponse', 'RejectedMessage', 'PendingContact',
    'Internal', 'External', 'PreSelected',
    'TalentAcquisitionInterview', 'EnglishTest', 'TechnicalTest',
    'EnglishLevel', 'TechnicalRequirements', 'Seniority',
    'CouldNotContact', 'Attitude', 'ByInternalClient',
    'ByExternalClient', 'SalaryExpectations',
    'DecidedNotToContinue', 'RejectedJobOffer', 'Other'
  ];
  return validSubStatuses.includes(subStatus);
};

const generateHeadCells = (canEdit: boolean): HeadCell[] => {
  const baseHeadCells: HeadCell[] = [
    {
      id: 'candidateName',
      numeric: true,
      disablePadding: false,
      label: 'Candidate name',
    },
    {
      id: 'status',
      numeric: false,
      disablePadding: true,
      label: 'Status',
    },
    {
      id: 'subStatus',
      numeric: false,
      disablePadding: true,
      label: 'Sub status',
    },
    {
      id: 'reason',
      numeric: false,
      disablePadding: true,
      label: 'Reason',
    },
    {
      id: 'salaryExpectations',
      numeric: true,
      disablePadding: false,
      label: 'Salary expectations',
    },
    {
      id: 'seniority',
      numeric: true,
      disablePadding: false,
      label: 'Seniority',
    },
    {
      id: 'country',
      numeric: true,
      disablePadding: false,
      label: 'Country',
    },
    {
      id: 'english',
      numeric: true,
      disablePadding: false,
      label: 'English level',
    },
    {
      id: 'recruiterResponsible',
      numeric: true,
      disablePadding: false,
      label: 'Recruiter responsible',
    },
  ];

  if (canEdit) {
    baseHeadCells.push({
      id: '',
      numeric: true,
      disablePadding: false,
      label: '',
    });
  }

  return baseHeadCells;
};

interface CandidateTableProps {
  handleSearch: (term: string) => void;
  status: string;
  subStatus: string;
  onStatusChange: (newStatus: string) => void;
  onSubStatusChange: (newStatus: string) => void;
  subStatusOptions: { label: string; value: string }[];
  handleClearFilters: () => void;
  clearSearch: boolean;
  rows: any[];
  totalItems: number;
  onInvalidateQuery: () => void;
  jobStatus: StatusType;
  loadMore: () => void;
  loading: boolean;
  hasMore: boolean;
}

export const CandidateTable = ({
  handleSearch,
  status,
  subStatus,
  onStatusChange,
  onSubStatusChange,
  subStatusOptions,
  handleClearFilters,
  clearSearch,
  rows,
  totalItems,
  onInvalidateQuery,
  jobStatus,
  loadMore,
  loading,
  hasMore,
}: CandidateTableProps) => {
  const { t } = useTranslation();
  const { jobId } = useParams();
  const navigate = useNavigate();

  const assignExistingCandidate = t('actions.assign');
  const assignNewCandidate = t('actions.create_new');

  const [showAssignCandidatesModal, setShowAssignCandidatesModal] = useState(false);
  const [showCreateCandidatesModal, setShowCreateCandidatesModal] = useState(false);
  const [candidateJobToDeleteId, setCandidateJobToDeleteId] = useState();
  const [showConfirmDeleteCandidateJobModal, setShowConfirmDeleteCandidateJobModal] =
    useState(false);

  const handleClick = (id: number) => {
    navigate(generatePath(paths.CANDIDATE_DETAIL_JOBS, { candidateId: `${id}` }));
  };

  const { mutate: deleteCandidateJobMutation } = useMutation({
    mutationFn: () => deleteCandidateJob(candidateJobToDeleteId),
    onError: () => {
      toast.error(t('toast.errors.something_went_wrong'));
    },
    onSuccess: () => {
      toast.success(t('toast.success.delete', { elem: 'Candidate' }));
      onInvalidateQuery();
    },
  });

  const handleDeleteCandidateJob = () => {
    deleteCandidateJobMutation();
    setShowConfirmDeleteCandidateJobModal(false);
  };

  const canEditCandidates = () => {
    return jobStatus !== JobStatus.Closed && jobStatus !== JobStatus.Canceled;
  }

  return (
    <div>
      <ActionsContainer>
        <Box display="flex" flexDirection="row" alignItems="start" gap={2}>
          {canEditCandidates() && (
            <Box display="flex" flexDirection="row" alignItems="start" gap={2}>
              <Button
                sx={{ height: 50 }}
                variant="contained"
                onClick={() => setShowCreateCandidatesModal(true)}
              >
                {assignNewCandidate}
              </Button>
              <Button
                sx={{ height: 50 }}
                variant="contained"
                onClick={() => setShowAssignCandidatesModal(true)}
              >
                {assignExistingCandidate}
              </Button>
            </Box>
          )}
          <ClearFiltersButton handleClearFilters={handleClearFilters} />
        </Box>
        <SearchAndFilterContainer>
          <SearchBar width="300px" onSearch={handleSearch} clearSearch={clearSearch} />
          <div style={{ display: 'flex' }}>
            <StatusSelect
              handleChange={(newStatus: string) => {
                onStatusChange(newStatus);
              }}
              status={status}
              label="Status"
              options={candidateJobStatusOptions}
            />
            <StatusSelect
              options={subStatusOptions}
              handleChange={(newStatus: string) => {
                onSubStatusChange(newStatus);
              }}
              status={subStatus}
              label="Sub status"
            />
          </div>
        </SearchAndFilterContainer>
      </ActionsContainer>
      <DashboardScrollTable
        headerCells={generateHeadCells(canEditCandidates())}
        rows={rows}
        loadMore={loadMore}
        hasMore={hasMore}
        rowContent={(index: number, row: any) => {
          const labelId = `enhanced-table-checkbox-${row.id}`;
          const candidateLink = generatePath(paths.CANDIDATE_DETAIL_JOBS, { candidateId: `${row.candidateId}` });

          return (
            <React.Fragment>
              <StyledCell onClick={() => handleClick(row.candidateId)}>{row.candidateName}</StyledCell>
              <StyledCell id={labelId} onClick={() => handleClick(row.candidateId)} scope="row">
                {t(`candidate_jobs.status.${row.generalData.status}`)}
              </StyledCell>
              <StyledCell id={labelId} onClick={() => handleClick(row.candidateId)} scope="row">
                {isValidSubStatus(row.generalData.subStatus)
                  ? t(`candidate_jobs.sub_status.${row.generalData.subStatus}`)
                  : '-'}
              </StyledCell>
              <StyledCell onClick={() => handleClick(row.candidateId)}>{row.generalData?.comment}</StyledCell>
              <StyledCell onClick={() => handleClick(row.candidateId)}>{row.generalData?.salaryExpectation}</StyledCell>
              <StyledCell onClick={() => handleClick(row.candidateId)}>{row.generalData?.seniority}</StyledCell>
              <StyledCell onClick={() => handleClick(row.candidateId)}>{row.candidateCountry}</StyledCell>
              <StyledCell onClick={() => handleClick(row.candidateId)}>{row.languageProficiencyData?.level}</StyledCell>
              <StyledCell onClick={() => handleClick(row.candidateId)}>{row.user?.fullName}</StyledCell>
              {canEditCandidates() && (
                <StyledCell>
                  <Tooltip title={t('jobs.assign.unassign_tooltip')}>
                    <IconButton
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setCandidateJobToDeleteId(row.id);
                        setShowConfirmDeleteCandidateJobModal(true);
                      }}
                    >
                      <RemoveCircleOutlineIcon />
                    </IconButton>
                  </Tooltip>
                </StyledCell>
              )}
            </React.Fragment>
          );
        }}
        totalItems={totalItems}
        pageNavigation={JOBS}
      />
      <AssignCandidateDialog
        isOpen={showAssignCandidatesModal}
        handleClose={() => setShowAssignCandidatesModal(false)}
        onInvalidateQuery={onInvalidateQuery}
      />
      <CandidateFormDialog
        jobId={jobId}
        open={showCreateCandidatesModal}
        handleClose={() => setShowCreateCandidatesModal(false)}
        onInvalidateQuery={onInvalidateQuery}
      />
      <ConfirmActionDialog
        open={showConfirmDeleteCandidateJobModal}
        handleClose={() => setShowConfirmDeleteCandidateJobModal(false)}
        handleConfirm={handleDeleteCandidateJob}
        description={t('jobs.assign.unassign_confirm_message')}
      />
    </div>
  );
};
